// vim: set ts=2 sts=2 sw=2 et:

import type { DmxConfig } from "./dmxConfig";
import type { PlatformLiveBridgeProviderId } from "./platformLiveBridgeClient";
import type { RecordMarkingMode } from "../records/recordMarkingPolicy";
import type { WrpfRecordCategory } from "../handoff/types";
import { isWrpfRecordCategory } from "../records/recordCategories";

export interface PlatformLiveFeatures {
  readonly judgeVoting: boolean;
  readonly spectatorDisplay: boolean;
  readonly streamingDisplays?: boolean;
  readonly dmx: boolean;
  /**
   * When true (DEFAULT), the operator must press "Confirmar veredicto" before the judges' verdict is
   * committed and revealed (válido/nulo + scoresheet). The live server still publishes judge lights for
   * operational consumers; public display clients keep them hidden during the pause. Uncheck it for
   * automatic resolution.
   */
  readonly manualVerdictConfirmation?: boolean;
}

export interface PlatformLiveProfile {
  /** Torneo dueño de esta sala. Parte de la room key (torneo, día, tarima) para no colisionar. */
  readonly tournamentId?: string;
  /** Bridge provider that owns this active room; absent for manual profiles without a bridge. */
  readonly bridgeProviderId?: PlatformLiveBridgeProviderId;
  /** Local DMX ownership for cloud rooms. Missing values are legacy bridge profiles. */
  readonly dmxConnection?: "legacyBridge" | "helper";
  readonly day: number;
  readonly platformId: number;
  readonly lanHost: string;
  readonly appPort: number;
  readonly livePort: number;
  readonly dmxConfig?: DmxConfig;
  readonly dmxConfigPath?: string;
  readonly features: PlatformLiveFeatures;
  readonly activatedAt: string;
}

export interface WrpfLiveState {
  readonly activeProfile: PlatformLiveProfile | null;
  /** Persisted only for a Helper profile; it must be verified before becoming active after reload. */
  readonly helperProfileCandidate: PlatformLiveProfile | null;
  readonly recordMarking: WrpfRecordMarkingState;
  readonly developerSettings: WrpfLiveDeveloperSettings;
}

export interface WrpfLiveDeveloperSettings {
  readonly recordTestToolsEnabled: boolean;
  readonly registrationPaginationEnabled: boolean;
  /** Last bridge provider selected in developer data; null uses the build's environment default. */
  readonly bridgeProviderId: PlatformLiveBridgeProviderId | null;
}

export interface WrpfRecordMarkingState {
  readonly mode: RecordMarkingMode;
  readonly overrides: Readonly<Record<string, WrpfRecordCategory | false>>;
  readonly categoriesWhenResolved: Readonly<Record<string, WrpfRecordCategory>>;
}

export const defaultPlatformLiveFeatures = (): PlatformLiveFeatures => ({
  judgeVoting: true,
  spectatorDisplay: true,
  streamingDisplays: false,
  dmx: false,
  manualVerdictConfirmation: false,
});

export const defaultWrpfLiveState = (): WrpfLiveState => ({
  activeProfile: null,
  helperProfileCandidate: null,
  recordMarking: {
    mode: "manual",
    overrides: {},
    categoriesWhenResolved: {},
  },
  developerSettings: {
    recordTestToolsEnabled: false,
    registrationPaginationEnabled: false,
    bridgeProviderId: null,
  },
});

export const normalizePlatformLiveFeatures = (features: PlatformLiveFeatures): PlatformLiveFeatures => ({
  ...features,
  streamingDisplays: typeof features.streamingDisplays === "boolean" ? features.streamingDisplays : false,
  manualVerdictConfirmation:
    typeof features.manualVerdictConfirmation === "boolean" ? features.manualVerdictConfirmation : false,
});

const isBridgeProviderId = (value: unknown): value is PlatformLiveBridgeProviderId =>
  value === "local" || value === "render" || value === "seenode";

const migrateBridgeProviderId = (profile: PlatformLiveProfile): PlatformLiveBridgeProviderId | undefined => {
  if (isBridgeProviderId(profile.bridgeProviderId)) return profile.bridgeProviderId;
  // Perfiles persistidos antes de v0.51 guardaban el proveedor cloud bajo `cloudProviderId`.
  const legacy = (profile as unknown as { cloudProviderId?: unknown }).cloudProviderId;
  return isBridgeProviderId(legacy) ? legacy : undefined;
};

export const normalizePlatformLiveProfile = (profile: PlatformLiveProfile): PlatformLiveProfile => {
  return {
    tournamentId: profile.tournamentId,
    bridgeProviderId: migrateBridgeProviderId(profile),
    dmxConnection:
      profile.dmxConnection === "helper" || profile.dmxConnection === "legacyBridge"
        ? profile.dmxConnection
        : undefined,
    day: profile.day,
    platformId: profile.platformId,
    lanHost: profile.lanHost,
    appPort: profile.appPort,
    livePort: profile.livePort,
    dmxConfig: profile.dmxConfig,
    dmxConfigPath: profile.dmxConfigPath,
    features: normalizePlatformLiveFeatures(profile.features),
    activatedAt: profile.activatedAt,
  };
};

const normalizeRecordMap = <T>(
  value: unknown,
  accepts: (candidate: unknown) => candidate is T,
): Readonly<Record<string, T>> => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return {};
  const source = value as Record<string, unknown>;
  const normalized = Object.entries(source).reduce<Record<string, T>>((result, [key, candidate]) => {
    if (accepts(candidate)) result[key] = candidate;
    return result;
  }, {});
  return Object.keys(normalized).length === Object.keys(source).length
    ? (value as Readonly<Record<string, T>>)
    : normalized;
};

export const normalizeWrpfLiveState = (state: WrpfLiveState): WrpfLiveState => {
  const incoming = state as Partial<WrpfLiveState>;
  const incomingRecordMarking = incoming.recordMarking as
    | (Partial<WrpfRecordMarkingState> & { readonly markedWhenResolved?: unknown })
    | undefined;
  const overrides = normalizeRecordMap(
    incomingRecordMarking?.overrides,
    (value): value is WrpfRecordCategory | false => value === false || isWrpfRecordCategory(value),
  );
  const categoriesWhenResolved = normalizeRecordMap(
    incomingRecordMarking?.categoriesWhenResolved,
    isWrpfRecordCategory,
  );
  const recordMarking: WrpfRecordMarkingState =
    incomingRecordMarking !== undefined &&
    (incomingRecordMarking.mode === "manual" || incomingRecordMarking.mode === "automatic") &&
    incomingRecordMarking.overrides === overrides &&
    incomingRecordMarking.categoriesWhenResolved === categoriesWhenResolved
      ? (incomingRecordMarking as WrpfRecordMarkingState)
      : {
          mode: incomingRecordMarking?.mode === "automatic" ? "automatic" : "manual",
          overrides,
          categoriesWhenResolved,
        };
  const incomingDeveloperSettings = incoming.developerSettings;
  const recordTestToolsEnabled =
    typeof incomingDeveloperSettings?.recordTestToolsEnabled === "boolean"
      ? incomingDeveloperSettings.recordTestToolsEnabled
      : false;
  const registrationPaginationEnabled =
    typeof incomingDeveloperSettings?.registrationPaginationEnabled === "boolean"
      ? incomingDeveloperSettings.registrationPaginationEnabled
      : false;
  const legacyProviderId = (incomingDeveloperSettings as unknown as { cloudProviderId?: unknown } | undefined)
    ?.cloudProviderId;
  const hasLegacyProviderKey =
    incomingDeveloperSettings !== undefined &&
    "cloudProviderId" in (incomingDeveloperSettings as unknown as Record<string, unknown>);
  const incomingBridgeProviderId = incomingDeveloperSettings?.bridgeProviderId;
  const bridgeProviderId = isBridgeProviderId(incomingBridgeProviderId)
    ? incomingBridgeProviderId
    : isBridgeProviderId(legacyProviderId)
      ? legacyProviderId
      : null;
  const developerSettings: WrpfLiveDeveloperSettings =
    typeof incomingDeveloperSettings?.recordTestToolsEnabled === "boolean" &&
    typeof incomingDeveloperSettings?.registrationPaginationEnabled === "boolean" &&
    !hasLegacyProviderKey &&
    (isBridgeProviderId(incomingDeveloperSettings?.bridgeProviderId) ||
      incomingDeveloperSettings?.bridgeProviderId === null ||
      incomingDeveloperSettings?.bridgeProviderId === undefined)
      ? incomingDeveloperSettings
      : { recordTestToolsEnabled, registrationPaginationEnabled, bridgeProviderId };
  const activeProfile = state.activeProfile === null ? null : normalizePlatformLiveProfile(state.activeProfile);
  const helperProfileCandidate =
    state.helperProfileCandidate?.dmxConnection === "helper"
      ? normalizePlatformLiveProfile(state.helperProfileCandidate)
      : null;
  if (
    activeProfile === state.activeProfile &&
    helperProfileCandidate === state.helperProfileCandidate &&
    incomingRecordMarking === recordMarking &&
    incomingDeveloperSettings === developerSettings
  ) {
    return state;
  }
  return { activeProfile, helperProfileCandidate, recordMarking, developerSettings };
};
